import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import type {ComposeMessageContext} from '@prompt-bridge/shared/validations';

type Fact = ComposeMessageContext['facts'][number];

export interface AiComposerFactFormatters {
  formatCurrency: (amount: number, currency: string, decimals: number) => string | null;
  formatNumber: (value: number) => string | null;
  formatDate: (value: Date) => string;
  formatDateTime: (value: Date) => string;
  parseDate: (value: string) => Date;
}

const PRICE_KEY = /(PriceWithVat|\.totalPrice)$/;
const DATE_KEY =
  /(\.completionAt|\.onlineOfferValidTo|\.reservedUntil|\.scheduledAt|^rentalOrder\.(start|end)|^replyTo\.receivedAt)$/;
const STATE_KEY = /\.state$/;
const MILEAGE_KEY = 'vehicle.mileage';
const CURRENCY_CODE = /^[A-Z]{3}$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MONEY_DECIMALS = 2;
const MILEAGE_UNIT = 'km';

/** The projection writes an amount as "<decimal> <ISO currency>", for example "900000 CZK". */
const formatAmount = (value: string, formatters: AiComposerFactFormatters) => {
  const [rawAmount, currency, ...rest] = value.split(' ');
  const amount = Number(rawAmount);
  if (
    isNil(rawAmount) ||
    rawAmount === '' ||
    isNil(currency) ||
    !CURRENCY_CODE.test(currency) ||
    rest.length > 0 ||
    !Number.isFinite(amount)
  ) {
    return value;
  }
  const decimals = Number.isInteger(amount) ? 0 : MONEY_DECIMALS;
  return formatters.formatCurrency(amount, currency, decimals) ?? value;
};

const formatDate = (value: string, formatters: AiComposerFactFormatters) => {
  const date = formatters.parseDate(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return DATE_ONLY.test(value) ? formatters.formatDate(date) : formatters.formatDateTime(date);
};

const formatMileage = (value: string, formatters: AiComposerFactFormatters) => {
  const mileage = Number(value);
  const formatted = Number.isFinite(mileage) ? formatters.formatNumber(mileage) : null;
  return isNil(formatted) ? value : `${formatted} ${MILEAGE_UNIT}`;
};

/**
 * The local facts keep the database form ("900000 CZK", "2026-10-14T08:00:00Z"); an employee reads
 * the customer form. A state is a code ("WORKDONE") with no label here, so it is not shown at all.
 */
export const formatAiComposerFactValue = (
  fact: Fact,
  formatters: AiComposerFactFormatters
): string | null =>
  match(fact.key)
    .when(
      (key) => STATE_KEY.test(key),
      () => null
    )
    .when(
      (key) => PRICE_KEY.test(key),
      () => formatAmount(fact.value, formatters)
    )
    .when(
      (key) => DATE_KEY.test(key),
      () => formatDate(fact.value, formatters)
    )
    .with(MILEAGE_KEY, () => formatMileage(fact.value, formatters))
    .otherwise(() => fact.value);
